import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  CategoriaTecnica,
  EstadoEventoTecnico,
  IEventoGenerico,
  IFilter,
  IListadoCategoria,
  IMaterialServicioTecnico,
  IQueryParam,
  IUsuario,
  TipoEventoGenerico,
  IUpdateEventoGenerico,
  ICreateEventoGenerico,
  RangoHorario,
  IValoresEventoTecnico,
} from 'modelos/src';
import { firstValueFrom, Subscription } from 'rxjs';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { LoginService } from '../../../../login/login.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { EventosGenericosHttpService } from '../../../../../auxiliares/servicios/http/eventos-genericos.service';
import { MaterialesServicioTecnicoService } from '../../../../../auxiliares/servicios/http/materiales-servicio-tecnico.service';
import { IUsuarioConPermiso } from '../../../../../auxiliares/servicios/http/usuarios.service';
import { tecnicoAtiendeTipo } from '../../../../../auxiliares/servicios/especialidad-tecnico';
import {
  CrearEditarUsuarioFuncionComponent,
  DataUsuarioFuncion,
} from '../../../../standalone/usuarios-por-funcion/crear-editar-usuario-funcion/crear-editar-usuario-funcion.component';
import {
  AsignarMaterialesTecnicoComponent,
  DataAsignarMaterialesTecnico,
} from '../../../../standalone/asignar-materiales-tecnico/asignar-materiales-tecnico.component';

export interface ICrearEditarEventoTecnicoGenericoData {
  id?: string;
  idEntidad?: string; // ID del dispositivo (alarma, tracker, activo, luminaria)
  tipoEvento?: TipoEventoGenerico; // Tipo de evento técnico
  categoria?: CategoriaTecnica;
  /**
   * @param idsCQAET - IDs de los clientes que pueden atender el Servicio Técnico
   */
  idsCQAET?: string[];
  /**
   * Si se pasa idTracker, el formulario mostrará un selector para elegir
   * si el evento se crea para el vehículo (idEntidad) o para el tracker (idTracker).
   */
  idTracker?: string;
}

type TipoEventoTecnico =
  | 'Evento Técnico Alarma'
  | 'Evento Técnico Vehículo'
  | 'Evento Técnico Tracker'
  | 'Evento Técnico Luminaria'
  | 'Evento Técnico Sirena'
  | 'Evento Técnico Gateway';

interface IEventoTecnicoGenericoForm {
  filtrador: FormControl<'Servicio Técnico' | 'Verificación'>;
  tipoEvento: FormControl<TipoEventoTecnico | null>;
  estado: FormControl<EstadoEventoTecnico | null>;
  valores: FormGroup<{
    titulo: FormControl<string | null>;
    mensaje: FormControl<string | null>;
    categoria: FormControl<CategoriaTecnica | null>;
    solicitante: FormGroup<{
      nombre: FormControl<string | null>;
      telefono: FormControl<string | null>;
      email: FormControl<string | null>;
    }>;
    disponibilidad: FormControl<RangoHorario[] | null>;
    prioridad: FormControl<'Normal' | 'Alta' | 'Urgente'>;
  }>;
  idEntidad: FormControl<string | null>;
  idsClientesQuePuedenAtender: FormControl<string[] | null>;
  idsClientesAtendiendo: FormControl<string[] | null>;
  detallesTecnicos: FormGroup<{
    idTecnicoAsignado: FormControl<string | null>;
  }>;
}

@Component({
  selector: 'app-crear-editar-evento-tecnico-generico',
  templateUrl: './crear-editar-evento-tecnico-generico.component.html',
  styleUrl: './crear-editar-evento-tecnico-generico.component.scss',
  standalone: false,
})
export class CrearEditarEventoTecnicoGenericoComponent
  implements OnInit, OnDestroy
{
  private formularios = inject(FormularioDialogService);
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private service = inject(EventosGenericosHttpService);
  private materialesService = inject(MaterialesServicioTecnicoService);
  private dialog = inject(MatDialog);

  public loading = signal(false);
  public data = inject(
    MAT_DIALOG_DATA,
  ) as ICrearEditarEventoTecnicoGenericoData;

  /** Selector vehículo vs tracker (solo visible cuando data.idTracker está presente) */
  public tipoEntidad: 'vehiculo' | 'tracker' = 'vehiculo';

  public form?: FormGroup<IEventoTecnicoGenericoForm>;
  public evento?: IEventoGenerico;
  public tecnicos: (IUsuario & { label: string })[] = [];
  // Opciones del selector de prioridad
  public readonly opcionesPrioridad = [
    { valor: 'Normal', etiqueta: 'Normal' },
    { valor: 'Alta', etiqueta: 'Alta' },
    { valor: 'Urgente', etiqueta: 'Urgente' },
  ];
  public verTodos = false;
  public eventoCreado?: IEventoGenerico;
  public mostrarResumen = signal(false);
  // Alta con técnico cuya asignación falló: el ST quedó creado sin técnico
  public asignacionFallida = signal(false);
  public fechaDisponibleParaTratar?: Date;
  public materialesTecnico: { nombre: string; cantidad: number }[] = [];

  private filtradorSub?: Subscription;
  private tecnicoSub?: Subscription;
  private tipoEventoSub?: Subscription;

  public cliente = LoginService.getCliente();

  public estados: EstadoEventoTecnico[] = [
    'Pendiente',
    'Asignado',
    'En Atención',
    'Pendiente de Aprobación',
    'Finalizado',
  ];

  public categorias: CategoriaTecnica[] = [
    'Alarma',
    'Tracker',
    'Luminaria',
    'Sirena',
    'Gateway',
    'Vehículo',
  ];

  private createForm() {
    const valoresEvento = this.evento?.tipoEvento?.startsWith('Evento Técnico')
      ? (this.evento?.valores as IValoresEventoTecnico)
      : undefined;

    this.form = new FormGroup({
      filtrador: new FormControl<'Servicio Técnico' | 'Verificación'>(
        (this.evento?.filtrador as 'Servicio Técnico' | 'Verificación') ||
          'Servicio Técnico',
        { nonNullable: true },
      ),
      tipoEvento: new FormControl<TipoEventoTecnico | null>(
        (this.evento?.tipoEvento as TipoEventoTecnico) ||
          (this.data.tipoEvento as TipoEventoTecnico) ||
          null,
        [Validators.required],
      ),
      estado: new FormControl<EstadoEventoTecnico>(
        (this.evento?.estado as EstadoEventoTecnico) || 'Pendiente',
        [Validators.required],
      ),
      valores: new FormGroup({
        titulo: new FormControl<string | null>(valoresEvento?.titulo ?? null, [
          Validators.required,
        ]),
        mensaje: new FormControl<string | null>(valoresEvento?.mensaje ?? null),
        categoria: new FormControl<CategoriaTecnica | null>(
          valoresEvento?.categoria || this.data.categoria || null,
        ),
        solicitante: new FormGroup({
          nombre: new FormControl<string | null>(
            valoresEvento?.solicitante?.nombre ?? null,
          ),
          telefono: new FormControl<string | null>(
            valoresEvento?.solicitante?.telefono ?? null,
          ),
          email: new FormControl<string | null>(
            valoresEvento?.solicitante?.email ?? null,
            [Validators.email],
          ),
        }),
        disponibilidad: new FormControl<RangoHorario[] | null>(
          valoresEvento?.disponibilidad ?? null,
        ),
        prioridad: new FormControl<'Normal' | 'Alta' | 'Urgente'>(
          valoresEvento?.prioridad || 'Normal',
          { nonNullable: true },
        ),
      }),
      idEntidad: new FormControl<string | null>(
        this.evento?.idEntidad || this.data.idEntidad || null,
        [Validators.required],
      ),
      idsClientesQuePuedenAtender: new FormControl<string[] | null>(
        this.evento?.idsClientesQuePuedenAtender ||
          this.cliente?.config?.idsClientesQuePuedenAtenderEventosTecnicos ||
          null,
      ),
      idsClientesAtendiendo: new FormControl<string[] | null>(
        this.evento?.idsClientesAtendiendo ?? null,
      ),
      detallesTecnicos: new FormGroup({
        idTecnicoAsignado: new FormControl<string | null>(
          this.evento?.detallesTecnicos?.idTecnicoAsignado ?? null,
        ),
      }),
    });
  }

  private async listar() {
    if (!this.data.id) return;
    this.evento = await this.service.getById(this.data.id);
  }

  // Etiqueta visible/buscable: "Nombre completo (usuario)". Sin nombre cae al usuario.
  private armarLabel(u: IUsuario): string {
    const nombre = u.datosPersonales?.nombre?.trim();
    return nombre ? `${nombre} (${u.usuario})` : (u.usuario ?? '');
  }

  private async listarTecnicos(): Promise<void> {
    // Análogo al modal de asignar técnico: includeChildren para alcanzar al cliente
    // (si es hijo) y se filtra por su idCliente exacto salvo que verTodos esté activo.
    const idCliente = this.verTodos
      ? undefined
      : this.evento?.idCliente || this.cliente?._id;
    const query: IQueryParam = {
      limit: 0,
      includeChildren: true,
      childrenLevel: 0,
      filter: JSON.stringify(idCliente ? { idCliente } : {}),
    };
    const datos =
      (await firstValueFrom(this.lista2.tecnicos(query))).datos || [];
    // El scope por cliente lo aplica el backend (query.filter + permiso). No se
    // re-filtra por u.idCliente: está deprecado y los técnicos auto-registrados
    // lo tienen vacío (su cliente sale del Permiso).
    this.tecnicos = datos
      .map((u) => ({ ...u, label: this.armarLabel(u) }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }),
      );
    this.filtrarTecnicos();
  }

  public async cambiarVerTodos(): Promise<void> {
    await this.listarTecnicos();
  }

  // Carga y agrupa los materiales ya asignados al técnico (para mostrarlos en el paso).
  private async cargarMaterialesTecnico(
    idUsuario?: string | null,
  ): Promise<void> {
    if (!idUsuario) {
      this.materialesTecnico = [];
      return;
    }
    const filter: IFilter<IMaterialServicioTecnico> = {
      idUsuario,
      tipo: 'asignado',
      estado: 'Asignado',
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      unlimited: true,
      includeChildren: true,
    };
    try {
      const res = await this.materialesService.getFiltered(query);
      const map = new Map<string, number>();
      for (const m of res.datos || []) {
        const nombre = m.descripcion || m.idEntidad || 'Material';
        map.set(nombre, (map.get(nombre) ?? 0) + (m.cantidad ?? 1));
      }
      this.materialesTecnico = [...map.entries()].map(([nombre, cantidad]) => ({
        nombre,
        cantidad,
      }));
    } catch (error) {
      console.error(error);
      this.materialesTecnico = [];
    }
  }

  // Técnicos que pueden atender este tipo de ST (según su especialidad/módulo).
  // Propiedad estable (NO getter): ng-select necesita una referencia de array
  // constante o se resetea la selección en cada ciclo de detección de cambios.
  public tecnicosFiltrados: (IUsuario & { label: string })[] = [];

  private filtrarTecnicos(): void {
    const tipo = this.form?.controls.tipoEvento.value;
    this.tecnicosFiltrados = this.tecnicos.filter((t) =>
      tecnicoAtiendeTipo((t as IUsuarioConPermiso).permiso, tipo),
    );
  }

  // Técnico actualmente seleccionado en el paso de asignación (objeto completo).
  public get tecnicoSeleccionado(): IUsuario | undefined {
    const id =
      this.form?.controls.detallesTecnicos.controls.idTecnicoAsignado.value;
    return id ? this.tecnicos.find((t) => t._id === id) : undefined;
  }

  // Abre el diálogo de asignar materiales para el técnico seleccionado. Los
  // materiales se guardan con idUsuario = técnico, independientes del ST.
  public abrirAsignarMateriales(): void {
    const tecnico = this.tecnicoSeleccionado;
    if (!tecnico?._id) return;
    const data: DataAsignarMaterialesTecnico = { usuario: tecnico };
    const ref = this.formularios.abrir(AsignarMaterialesTecnicoComponent, {
      tamano: 'grande',
      data,
    });
    // Al cerrar, refrescar la lista de materiales del técnico.
    ref
      .afterClosed()
      .subscribe(() => this.cargarMaterialesTecnico(tecnico._id));
  }

  private getData(): ICreateEventoGenerico | IUpdateEventoGenerico | null {
    if (!this.form || !this.form.valid) {
      return null;
    }
    return this.form.getRawValue() as
      | ICreateEventoGenerico
      | IUpdateEventoGenerico;
  }

  public async onSubmit() {
    const res = await this.dialogService.confirm(
      `${this.data?.id ? 'Editar' : 'Crear'} Servicio Técnico`,
      `¿Querés ${this.data?.id ? 'editar' : 'crear'} el Servicio Técnico?`,
    );
    if (!res) return;

    const data = this.getData();
    if (!data) return;

    // Fecha disponible para tratar → ISO en detallesTecnicos (fuera del form).
    const fechaISO = this.fechaDisponibleParaTratar?.toISOString() ?? null;
    (data as ICreateEventoGenerico).detallesTecnicos = {
      ...(data as ICreateEventoGenerico).detallesTecnicos,
      fechaDisponibleParaTratar: fechaISO,
    };

    try {
      this.loading.set(true);
      let resultado: IEventoGenerico;

      if (this.data.id) {
        // Cambio de técnico: como al crear, va por asignar(), que pone el
        // estado y avisa por push al técnico nuevo (o al anterior si se lo
        // saca). El update guarda el resto con el técnico anterior, para que
        // asignar() sepa a quién se le sacó.
        const idTecnicoAnterior =
          this.evento?.detallesTecnicos?.idTecnicoAsignado ?? null;
        const idTecnicoNuevo =
          this.form?.controls.detallesTecnicos.controls.idTecnicoAsignado
            .value ?? null;
        const cambiaTecnico = idTecnicoNuevo !== idTecnicoAnterior;
        if (cambiaTecnico) {
          (data as IUpdateEventoGenerico).detallesTecnicos = {
            ...(data as IUpdateEventoGenerico).detallesTecnicos,
            idTecnicoAsignado: idTecnicoAnterior,
          };
        }
        resultado = await this.service.update(
          this.data.id,
          data as IUpdateEventoGenerico,
        );
        if (cambiaTecnico) {
          await this.service.asignar(
            this.data.id,
            idTecnicoNuevo as string,
            fechaISO ?? undefined,
          );
          resultado = await this.service.getById(this.data.id);
        }
        this.helper.notifSuccess('Servicio Técnico actualizado');
      } else {
        // El técnico no va en el create sino por asignar(), que valida que pueda
        // ver la entidad, pone el estado 'Asignado' y le avisa por push. Así, si
        // la asignación falla, el ST queda creado sin técnico (Pendiente) en vez
        // de con técnico pero Pendiente y sin aviso.
        const idTecnico =
          this.form?.controls.detallesTecnicos.controls.idTecnicoAsignado.value;
        const datosCrear = data as ICreateEventoGenerico;
        resultado = await this.service.create({
          ...datosCrear,
          detallesTecnicos: {
            ...datosCrear.detallesTecnicos,
            idTecnicoAsignado: null,
          },
        });
        let errorAsignar: unknown;
        if (idTecnico && resultado._id) {
          try {
            await this.service.asignar(
              resultado._id,
              idTecnico,
              fechaISO ?? undefined,
            );
          } catch (error) {
            errorAsignar = error;
          }
          resultado = await this.service.getById(resultado._id);
        }
        if (errorAsignar) {
          // Creado igual: el mensaje del backend dice por qué no se asignó
          console.error(errorAsignar);
          this.helper.notifError(errorAsignar);
          this.asignacionFallida.set(true);
        } else {
          this.helper.notifSuccess('Servicio Técnico creado');
        }
      }

      this.eventoCreado = resultado;
      this.mostrarResumen.set(true);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    } finally {
      this.loading.set(false);
    }
  }

  public cerrarDialog() {
    const dialogRef = this.dialog.getDialogById(
      this.dialog.openDialogs[this.dialog.openDialogs.length - 1]?.id || '',
    );
    dialogRef?.close(true);
  }

  public getValoresTecnicos(): IValoresEventoTecnico | null {
    if (
      this.eventoCreado &&
      this.eventoCreado.tipoEvento?.startsWith('Evento Técnico')
    ) {
      return this.eventoCreado.valores as IValoresEventoTecnico;
    }
    return null;
  }

  public categoriaTituloActual = signal<string | null>(null);

  private resolverCategoriaTitulo(
    tipoEvento: string | null | undefined,
  ): string | null {
    switch (tipoEvento) {
      case 'Evento Técnico Alarma':
        return 'Evento-Tecnico-Alarma';
      case 'Evento Técnico Tracker':
        return 'Evento-Tecnico-Tracker';
      case 'Evento Técnico Vehículo':
        return 'Evento-Tecnico-Vehiculo';
      case 'Evento Técnico Luminaria':
        return 'Evento-Tecnico-Luminaria';
      case 'Evento Técnico Sirena':
        return 'Evento-Tecnico-Sirena';
      case 'Evento Técnico Gateway':
        return 'Evento-Tecnico-Gateway';
      default:
        return null;
    }
  }

  public tituloInitialNames: string[] = [];

  public onTituloChange(cat: IListadoCategoria | null): void {
    this.form?.controls.valores.controls.titulo.setValue(cat?.nombre ?? null);
    this.form?.controls.valores.controls.titulo.markAsTouched();
  }

  public onTipoEntidadChange(tipo: 'vehiculo' | 'tracker'): void {
    this.tipoEntidad = tipo;
    if (tipo === 'tracker') {
      this.form?.controls.idEntidad.setValue(this.data.idTracker ?? null);
      this.form?.controls.tipoEvento.setValue('Evento Técnico Tracker');
      this.form?.controls.valores.controls.categoria.setValue('Tracker');
      this.categoriaTituloActual.set('Evento-Tecnico-Tracker');
    } else {
      this.form?.controls.idEntidad.setValue(this.data.idEntidad ?? null);
      this.form?.controls.tipoEvento.setValue('Evento Técnico Vehículo');
      this.form?.controls.valores.controls.categoria.setValue('Vehículo');
      this.categoriaTituloActual.set('Evento-Tecnico-Vehiculo');
    }
    this.form?.controls.valores.controls.titulo.setValue(null);
  }

  public async onAddTecnico() {
    const data: DataUsuarioFuncion = {
      editando: false,
      idCliente: this.cliente?._id || '',
      funcion: 'Técnico',
    };
    const ref = this.formularios.abrir(CrearEditarUsuarioFuncionComponent, {
      tamano: 'grande',
      data,
    });
    // Al cerrar, recargar técnicos para poder seleccionar el recién creado.
    ref.afterClosed().subscribe(() => this.listarTecnicos());
  }

  async ngOnInit() {
    await this.listarTecnicos();
    if (this.data?.id) {
      await this.listar();
      const titulo = (this.evento?.valores as IValoresEventoTecnico)?.titulo;
      this.tituloInitialNames = titulo ? [titulo] : [];
      const fecha = this.evento?.detallesTecnicos?.fechaDisponibleParaTratar;
      if (fecha) this.fechaDisponibleParaTratar = new Date(fecha);
    }
    this.createForm();
    this.categoriaTituloActual.set(
      this.resolverCategoriaTitulo(
        this.evento?.tipoEvento ?? this.data.tipoEvento ?? null,
      ),
    );

    this.filtradorSub = this.form?.controls.filtrador.valueChanges.subscribe(
      (val) => {
        const tituloCtrl = this.form?.controls.valores.controls.titulo;
        if (val === 'Verificación') {
          tituloCtrl?.setValue('Verificación');
        } else if (tituloCtrl?.value === 'Verificación') {
          tituloCtrl?.setValue(null);
        }
      },
    );

    // Materiales del técnico: carga inicial (edición) + al cambiar la selección.
    const tecnicoCtrl =
      this.form?.controls.detallesTecnicos.controls.idTecnicoAsignado;
    await this.cargarMaterialesTecnico(tecnicoCtrl?.value);
    this.tecnicoSub = tecnicoCtrl?.valueChanges.subscribe((id) =>
      this.cargarMaterialesTecnico(id),
    );

    // Re-filtrar técnicos según el tipo de evento (filtro inicial + al cambiar).
    this.filtrarTecnicos();
    this.tipoEventoSub = this.form?.controls.tipoEvento.valueChanges.subscribe(
      () => this.filtrarTecnicos(),
    );
  }

  ngOnDestroy() {
    this.filtradorSub?.unsubscribe();
    this.tecnicoSub?.unsubscribe();
    this.tipoEventoSub?.unsubscribe();
  }
}
